/**
 * Date utilities for ToToDo.
 *
 * All database date fields are stored as STRINGIFIED UTC MILLISECOND TIMESTAMPS.
 * Pattern: dayjs().utc().valueOf().toString() → "1739577600000"
 *
 * See docs/date-handling.md for full specification.
 */

import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { db } from "@/db/schema"
import { parseDbDate } from "@totodo/operations"

dayjs.extend(utc)

// ---------------------------------------------------------------------------
// Evening detection — ported from v1
// ---------------------------------------------------------------------------

/**
 * Default hour (24h) when evening mode activates.
 * At or after this hour, tomorrow's evening tasks are "pulled forward" to tonight,
 * and today's evening tasks graduate into the Today view.
 *
 * Overridden by the `#evening-time` user setting when available.
 */
export const EVENING_TIME = 18 // 6 PM

/**
 * Read the user's configured evening hour from `db.setting`.
 * Falls back to {@link EVENING_TIME} (18) if no setting exists.
 *
 * Use this in async code (queries, callbacks). For React components,
 * prefer `useSetting<number>("#evening-time", 18)` instead.
 */
export async function getEveningHour(): Promise<number> {
  try {
    const row = await db.setting.get("#evening-time")
    if (row?.value) {
      const parsed = parseInt(row.value, 10)
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 23) return parsed
    }
  } catch {
    // DB not ready or offline — fall back
  }
  return EVENING_TIME
}

/**
 * @deprecated Use `isEvening` from `@/db/operations` (single source of truth in @totodo/operations).
 * Re-exported here for backward compatibility.
 */
export { isEvening } from "@/db/operations"

/**
 * Current UTC timestamp as string — the standard format for all DB date fields.
 * @returns Stringified millisecond timestamp, e.g. "1739577600000"
 */
export function now(): string {
  return dayjs().utc().valueOf().toString()
}

/**
 * Convert a date/dayjs to the DB string format.
 */
export function toDbDate(date: dayjs.Dayjs | Date): string {
  return dayjs(date).utc().valueOf().toString()
}

/**
 * Convert a local Date to UTC midnight of the same calendar day.
 * Use when a date picker returns local midnight and you need UTC midnight
 * for the same calendar day (e.g., June 8 local → June 8 00:00 UTC).
 */
export function toUtcMidnight(date: Date): number {
  return dayjs(date).utc(true).startOf("day").valueOf()
}

/**
 * Parse a DB date string back to dayjs. Tolerates rows written as ISO (see `parseDbDate`); a value
 * that is empty or unparseable gives an INVALID dayjs (never today/epoch) - use {@link parseDbDayjs}
 * when "no date" must be told apart.
 */
export function fromDbDate(dbDate: string): dayjs.Dayjs {
  return dayjs(parseDbDate(dbDate) ?? NaN)
}

/**
 * Like {@link fromDbDate}, but `undefined` when the stored value is missing or unparseable, so a
 * caller can treat it as "no date" instead of showing today or an invalid date.
 */
export function parseDbDayjs(dbDate: string | number | undefined | null): dayjs.Dayjs | undefined {
  const ms = parseDbDate(dbDate)
  return ms === undefined ? undefined : dayjs(ms)
}

// ---------------------------------------------------------------------------
// Relative date text — ported from v1 getDeadlineText
// ---------------------------------------------------------------------------

/**
 * Get relative deadline text for display.
 *
 * @param dateStr - Timestamp string (ms since epoch) or undefined
 * @param short - Use short format ("3d" instead of "3 days")
 * @returns Human-readable relative date string
 */
export function getDeadlineText(
  dateStr: string | undefined,
  short = true,
): string {
  if (!dateStr) return ""

  const date = parseDbDayjs(dateStr)
  if (!date) return ""
  const today = dayjs()
  const diffDays = date.startOf("day").diff(today.startOf("day"), "day")

  if (diffDays === 0) return "Today"
  if (diffDays === 1) return short ? "Tmrw" : "Tomorrow"
  if (diffDays === -1) return short ? "Yest" : "Yesterday"

  if (diffDays > 1 && diffDays <= 7)
    return short ? `${diffDays}d` : `${diffDays} days`
  if (diffDays < -1 && diffDays >= -7)
    return short ? `${Math.abs(diffDays)}d` : `${Math.abs(diffDays)} days ago`

  // Beyond a week — short mode uses compact "Xd" / "Xd ago" format
  if (short) {
    if (diffDays > 7) return `${diffDays}d`
    if (diffDays < -7) return `${Math.abs(diffDays)}d`
  }

  // Long mode — use date format
  if (date.year() !== today.year()) {
    return date.format("MMM D, YYYY")
  }
  return date.format("MMM D")
}

/**
 * Format a start-at date for the compact date chip.
 * Uses short month format: "16 feb", "03 mar", etc.
 */
export function formatStartAtChip(dateStr: string): string {
  const date = parseDbDayjs(dateStr)
  if (!date) return ""
  return `${date.format("DD")} ${date.format("MMM").toLowerCase()}`
}

/**
 * Check if a date string is today or in the past.
 */
export function isTodayOrPast(dateStr: string): boolean {
  const date = parseDbDayjs(dateStr)
  return date ? !date.isAfter(dayjs(), "day") : false
}

/**
 * Check if a date string is exactly today.
 */
export function isExactlyToday(dateStr: string): boolean {
  return parseDbDayjs(dateStr)?.isSame(dayjs(), "day") ?? false
}

/**
 * Check if a date string is strictly before today (overdue).
 */
export function isPastDate(dateStr: string): boolean {
  return parseDbDayjs(dateStr)?.isBefore(dayjs(), "day") ?? false
}

/**
 * Check if a date string is in the future (after today).
 */
export function isFuture(dateStr: string): boolean {
  return parseDbDayjs(dateStr)?.isAfter(dayjs(), "day") ?? false
}

/**
 * Parse a DB date string to a JavaScript Date.
 */
export function fromDbDateToDate(dbDate: string): Date {
  return new Date(parseDbDate(dbDate) ?? NaN)
}

// ---------------------------------------------------------------------------
// 12/24h format detection — ported from v1 ReminderPicker
// ---------------------------------------------------------------------------

/**
 * Auto-detect whether the user's locale uses 12h or 24h time format.
 *
 * Uses `Intl.DateTimeFormat` to format a 13:00 timestamp and checks for
 * AM/PM markers (covers English, Japanese, Korean, Arabic, etc.).
 *
 * @returns true if 24h format, false if 12h
 */
export function is24HourFormat(): boolean {
  try {
    const formatted = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      hour12: undefined, // Let the locale decide
    }).format(new Date(2024, 0, 1, 13, 0, 0))

    // If the formatted string contains AM/PM markers, it's 12h format
    return !/am|pm|午前|午後|오전|오후|ص|م/i.test(formatted)
  } catch {
    return true // Default to 24h
  }
}

// ---------------------------------------------------------------------------
// Reminder time helpers
// ---------------------------------------------------------------------------

/**
 * Round up to the next whole hour.
 * e.g., 14:23 → 15:00, 14:00 → 15:00
 */
export function nextWholeHour(d: dayjs.Dayjs = dayjs()): dayjs.Dayjs {
  return d.add(1, "hour").startOf("hour")
}

/**
 * Get a "later today" time: now + 3h, rounded to hour start.
 * If that would be before 18:00, use 18:00 instead.
 */
export function laterTodayTime(): dayjs.Dayjs {
  const candidate = dayjs().add(3, "hour").startOf("hour")
  const sixPM = dayjs().hour(18).minute(0).second(0)
  return candidate.isBefore(sixPM) ? sixPM : candidate
}
